import uniq from 'lodash/uniq';

import { AdjustmentTypes, PricingPolicy } from '../../lib/products';
import { getIdFromGid } from '../../utils/gid-utils';

const DYNAMIC_BUNDLE = {
  BUNDLE_UID: '_bundleUid',
  PRODUCT_ID: '_bundleProductId',
  BUNDLE_NAME: '_bundleName',
  BUNDLE_TYPE: '_bundleType',
  BUNDLE_LABEL: '_bundleLabel',
};

type LineDiscount = {
  amount: string;
  currencyCode: string;
};

type Attribute = {
  key: string;
  value: string;
};

type CurrentPrice = {
  amount: string;
  currencyCode: string;
};

type Image = {
  transformedSrc: string;
};

type ImageMapType = Record<string, string>;
export type SCLine = {
  subscriptionLineId?: string;
  variantId: string;
  quantity: number;
  variantImage: Image;
  currentPrice: CurrentPrice;
  title: string;
  variantTitle: string;
  productId: string;
  pricingPolicy: PricingPolicy | null;
  lineDiscountedPrice: LineDiscount;
  discountAllocations: DiscountAllocations[];
  id: string;
  customAttributes: Attribute[];
  isDynamicBaB?: boolean;
};

const defaultLine = {
  variantId: '',
  quantity: 0,
  variantImage: {},
  currentPrice: '',
  title: '',
  variantTitle: '',
  productId: '',
  pricingPolicy: {},
  lineDiscountedPrice: {},
  discountAllocations: [],
  id: '',
  customAttributes: [],
};

const calculatePercentageDiscount = (basePrice, discount) =>
  parseFloat(basePrice) * (1 - discount / 100);

const calculateFixedDiscount = (basePrice, discount) => {
  const discounted = parseFloat(basePrice) - discount;
  return Math.max(0, discounted);
};

const calculateDiscountedPrice = (discount, price: number): number => {
  switch (discount.adjustmentType) {
    case AdjustmentTypes.PERCENTAGE:
      return calculatePercentageDiscount(
        price,
        discount.adjustmentValue.percentage,
      );

    case AdjustmentTypes.FIXED_AMOUNT:
      return calculateFixedDiscount(price, discount.adjustmentValue.amount);
    default:
      return price;
  }
};

export const isDynamicLine = (line) =>
  line?.customAttributes?.find(
    (attr) =>
      attr.key === DYNAMIC_BUNDLE.BUNDLE_TYPE && attr.value === 'dynamicBaB',
  );

export const isDynamicBundle = (bundle) => bundle?.type === 'dynamicBaB';

export const hasDynamicLines = (lines, changes?: any[]): boolean => {
  if (!lines || !lines.length) {
    return false;
  }

  return lines.some(
    (line) =>
      !!isDynamicLine(line) &&
      (!changes || changes?.map((c) => c.id).includes(line.id)),
  );
};

export const getLineBundleCustomAttribute = (line, key) => {
  return (
    line?.CustomAttributes?.find((attr) => attr.key === key) ||
    line?.customAttributes?.find((attr) => attr.key === key)
  );
};

export const getLineBundleUUID = (line) => {
  return getLineBundleCustomAttribute(line, DYNAMIC_BUNDLE.BUNDLE_UID);
};

const getLineBundleProductId = (line) => {
  return getLineBundleCustomAttribute(line, DYNAMIC_BUNDLE.PRODUCT_ID);
};

export const getDynamicBaBUIds = (lines: SCLine[]): string[] => {
  const dynamicBaBLines = lines.filter(isDynamicLine);
  if (!dynamicBaBLines.length) {
    return [];
  }

  const UUIDs = dynamicBaBLines
    .map((line) => {
      const bundleUID = getLineBundleUUID(line);
      return bundleUID && bundleUID.value;
    })
    .filter(Boolean);

  return uniq(UUIDs);
};

export const getDynamicBundleProductsByUid = (
  lines: SCLine[],
  id: string,
): SCLine[] => {
  const dynamicBaBLines = lines.filter(isDynamicLine);
  if (!dynamicBaBLines.length || !id) {
    return [];
  }

  return dynamicBaBLines
    .filter((line) => {
      const bundleUID = getLineBundleUUID(line);
      return bundleUID && bundleUID.value && bundleUID.value === id;
    })
    .filter(Boolean);
};

export const getDynamicBaBProductIds = (lines: SCLine[] = []): string[] => {
  const dynamicBaBLines = lines.filter(isDynamicLine);
  if (!dynamicBaBLines.length) {
    return [];
  }

  const productIds = dynamicBaBLines
    .map((line) => {
      const productId = getLineBundleProductId(line);
      return productId && productId.value;
    })
    .filter(Boolean);

  return uniq(productIds);
};

const getLineDiscountedPrice = (dynamicLines: SCLine[]) => {
  const result = dynamicLines.reduce((acc, line: SCLine) => {
    const amount = acc.amount
      ? parseFloat(acc.amount) + parseFloat(line.lineDiscountedPrice.amount)
      : parseFloat(line.lineDiscountedPrice.amount);
    return {
      ...acc,
      ...line.lineDiscountedPrice,
      amount: amount.toString(),
    };
  }, {} as LineDiscount);

  return {
    ...result,
    amount: parseFloat(result.amount).toFixed(2),
  };
};

const getLineBasePrice = (dynamicLines) => {
  const result = dynamicLines.reduce((acc, line) => {
    const price =
      line.pricingPolicy?.basePrice?.amount ||
      line.currentPrice?.amount ||
      line.lineDiscountedPrice?.amount;

    return {
      ...acc,
      ...line.pricingPolicy?.basePrice,
      amount: acc.amount
        ? parseFloat(acc.amount) + parseFloat(price) * line.quantity
        : parseFloat(price) * line.quantity,
    };
  }, {});

  return {
    ...result,
    amount: result.amount.toFixed(2),
  };
};

const getLineCycleDiscounts = (dynamicLines, basePrice) => {
  const line = dynamicLines.find(
    (line = {} as SCLine) => line.pricingPolicy?.cycleDiscounts,
  );
  return (
    line?.pricingPolicy?.cycleDiscounts?.map((d) => {
      return {
        ...d,
        computedPrice: {
          ...d.computedPrice,
          amount: calculateDiscountedPrice(d, basePrice.amount).toFixed(2),
        },
      };
    }) || []
  );
};

const getLineCurrentPrice = (dynamicLines) => {
  const result = dynamicLines.reduce((acc, line) => {
    return {
      ...acc,
      ...line.currentPrice,
      amount: acc.amount
        ? parseFloat(acc.amount) +
          parseFloat(line.currentPrice.amount) * line.quantity
        : parseFloat(line.currentPrice.amount) * line.quantity,
    };
  }, {});

  return {
    ...result,
    amount: result.amount.toFixed(2),
  };
};

const getCustomAttributes = (dynamicLines, customAttributes) => {
  const lineCustomAttributes = dynamicLines.reduce((acc, line) => {
    const pv = line.variantId ? getIdFromGid(line.variantId) : '';
    const sclId = getIdFromGid(line.id);
    const lineTitle = `${line.title}${
      line.variantTitle ? ` - ${line.variantTitle}` : ''
    }`;

    return [
      ...acc,
      { key: lineTitle, value: line.quantity },
      { key: `_pv_${pv}`, value: line.quantity },
      { key: `_scl_${sclId}`, value: pv },
    ];
  }, customAttributes);

  //check for repeated lines and sum the quantity

  const customAttributesLineInformation =
    sumSameProductVariants(lineCustomAttributes);
  return customAttributesLineInformation;
};

const getDiscounts = (dynamicLines) =>
  dynamicLines.reduce((acc, l) => [...acc, ...l.discountAllocations], []);

const getNewDynamicLine = (
  dynamicLines: SCLine[],
  images: ImageMapType,
): SCLine => {
  const baseLine = dynamicLines[0];
  if (!baseLine) {
    return;
  }

  const getCustomAttribute = (key: string): string =>
    baseLine.customAttributes?.find((attr) => attr.key === key)?.value || '';

  const newLine: SCLine = {
    ...defaultLine,
    ...baseLine,
    subscriptionLineId: baseLine.id,
    id: getLineBundleUUID(baseLine)?.value || '',
    variantTitle: getCustomAttribute(DYNAMIC_BUNDLE.BUNDLE_LABEL),
    quantity: 1,
    productId: getCustomAttribute(DYNAMIC_BUNDLE.PRODUCT_ID),
    title: getCustomAttribute(DYNAMIC_BUNDLE.BUNDLE_NAME),
    isDynamicBaB: true,
    lineDiscountedPrice: getLineDiscountedPrice(dynamicLines),
    currentPrice: getLineCurrentPrice(dynamicLines),
    discountAllocations: getDiscounts(dynamicLines),
  };

  newLine.variantImage = {
    transformedSrc: images[newLine.productId] || '',
  };

  const basePrice = getLineBasePrice(dynamicLines);
  newLine.pricingPolicy = {
    basePrice,
    cycleDiscounts: getLineCycleDiscounts(dynamicLines, basePrice),
  };

  newLine.customAttributes = getCustomAttributes(
    dynamicLines,
    newLine.customAttributes || [],
  );

  return newLine;
};

export const wrapDynamicLines = (
  lines: SCLine[],
  images: ImageMapType,
): SCLine[] => {
  if (!hasDynamicLines(lines)) {
    return lines;
  }

  const dynamicBaBUIds = getDynamicBaBUIds(lines);
  const nonDynamicBaBLines = lines
    .filter((line) => !isDynamicLine(line))
    .map((l) => ({ ...l, hideLine: true }));

  const dynamicBaBLines = lines.filter(isDynamicLine);
  const dynamicItems = dynamicBaBUIds.reduce((acc: SCLine[], uuid) => {
    const dynamicLines = dynamicBaBLines.filter((line) => {
      const bundleUID = getLineBundleUUID(line);
      return bundleUID?.value === uuid;
    });

    if (dynamicLines.length > 0) {
      return [...acc, getNewDynamicLine(dynamicLines, images)];
    }

    return acc;
  }, []);

  return [...nonDynamicBaBLines, ...dynamicItems];
};

export const sumSameProductVariants = (
  lineCustomAttributes: Array<{ key: string; value: string }>,
) => {
  const result = new Map<string, string | string>();

  for (const { key, value } of lineCustomAttributes) {
    if (!key.startsWith('_') || key.startsWith('_pv_')) {
      const numericValue =
        typeof value === 'string' ? parseInt(value, 10) : value;
      if (!isNaN(numericValue)) {
        result.set(key, numericValue.toString());
      }
    } else {
      result.set(key, value);
    }
  }

  return Array.from(result, ([key, value]) => ({ key, value }));
};
